/**
 * Attached files: image thumbnails that open in a lightbox (arrow keys move between images),
 * other files as chips that download. Remove is offered per file when the caller allows it.
 */
import * as RD from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Download, File as FileIcon, FileArchive, FileCode, FileText, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatBytes } from '@orgus/protocol';
import { cn } from '../lib/cn.ts';
import type { AttachmentView } from './AttachmentInput.tsx';
import { IconButton } from './IconButton.tsx';
import { Pressable } from './Pressable.tsx';

export interface AttachmentListProps {
  items: AttachmentView[];
  /** Offer Remove on the files this returns true for. */
  canRemove?: (a: AttachmentView) => boolean;
  onRemove?: (a: AttachmentView) => void;
  /** Ids being removed (their tile shows as busy). */
  removing?: readonly string[];
  /** Open this image in the lightbox (controlled from outside, for Markdown image clicks). */
  openId?: string | null;
  onOpenChange?: (id: string | null) => void;
  /** Images the lightbox steps through (default: the images listed). Pass more to open images shown elsewhere, such as inline in Markdown. */
  lightboxItems?: AttachmentView[];
  /** Shown when there are no files. Nothing is rendered when omitted. */
  empty?: string;
  /** 'chips': every file, images too, as one small pill each, inline (a composer's drafts). */
  variant?: 'list' | 'chips';
  className?: string;
}

function iconFor(a: AttachmentView) {
  if (a.mime === 'text/plain') return <FileText aria-hidden />;
  if (a.mime === 'application/zip' || a.mime === 'application/gzip') return <FileArchive aria-hidden />;
  if (a.mime === 'application/pdf') return <FileText aria-hidden />;
  if (/\.(html?|svg|xml|js|ts|json|css)$/i.test(a.name)) return <FileCode aria-hidden />;
  return <FileIcon aria-hidden />;
}

export function AttachmentList({ items, canRemove, onRemove, removing = [], openId, onOpenChange, lightboxItems, empty, variant = 'list', className }: AttachmentListProps) {
  const [ownOpen, setOwnOpen] = useState<string | null>(null);
  const open = openId !== undefined ? openId : ownOpen;
  const setOpen = (id: string | null) => (onOpenChange ? onOpenChange(id) : setOwnOpen(id));
  const images = items.filter((a) => a.isImage);
  const files = items.filter((a) => !a.isImage);
  const boxed = lightboxItems?.filter((a) => a.isImage) ?? images;
  const lightbox = <Lightbox images={boxed} openId={open && boxed.some((a) => a.id === open) ? open : null} onOpenChange={setOpen} />;
  if (!items.length) return empty ? <p className="text-sm text-fg-subtle">{empty}</p> : lightboxItems ? lightbox : null;

  if (variant === 'chips') {
    return (
      <>
        {items.map((a) => (
          <span key={a.id} data-attachments="" className={cn('inline-flex h-6 max-w-48 items-center gap-1 rounded-pill border border-border-subtle bg-surface pl-1 pr-0.5 text-caption', removing.includes(a.id) && 'opacity-50', className)}>
            {a.isImage ? (
              <Pressable onClick={() => setOpen(a.id)} aria-label={`Open ${a.name}`} className="flex shrink-0 cursor-zoom-in">
                <img src={a.thumbUrl ?? a.url} alt="" loading="lazy" decoding="async" className="size-4 rounded-pill object-cover" />
              </Pressable>
            ) : (
              <span className="flex shrink-0 pl-1 text-fg-subtle [&_svg]:size-3">{iconFor(a)}</span>
            )}
            <a href={a.downloadUrl} download={a.name} className="min-w-0 truncate text-fg-muted hover:text-fg" title={`${a.name}, ${formatBytes(a.size)}`}>
              {a.name}
            </a>
            {onRemove && canRemove?.(a) ? (
              <IconButton label={`Remove ${a.name}`} icon={<X />} size="xs" onClick={() => onRemove(a)} disabled={removing.includes(a.id)} />
            ) : (
              <span className="shrink-0 pr-1.5 text-fg-subtle num">{formatBytes(a.size)}</span>
            )}
          </span>
        ))}
        {lightbox}
      </>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)} data-attachments="">
      {images.length > 0 && (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2">
          {images.map((a) => (
            <li key={a.id} className={cn('group relative overflow-hidden rounded-md border border-border-subtle bg-bg', removing.includes(a.id) && 'opacity-50')}>
              <button type="button" onClick={() => setOpen(a.id)} className="block aspect-[4/3] w-full cursor-zoom-in focus-visible:outline-offset-[-2px]" aria-label={`Open ${a.name}`}>
                <img src={a.thumbUrl ?? a.url} alt={a.name} loading="lazy" decoding="async" className="size-full object-cover" />
              </button>
              <div className="flex items-center gap-1 border-t border-border-subtle px-1.5 py-1">
                <span className="min-w-0 flex-1 truncate text-xs text-fg-muted" title={a.name}>
                  {a.name}
                </span>
                {onRemove && canRemove?.(a) && (
                  <IconButton label={`Remove ${a.name}`} icon={<Trash2 />} size="xs" variant="danger" onClick={() => onRemove(a)} disabled={removing.includes(a.id)} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {files.length > 0 && (
        <ul className="flex flex-col gap-1">
          {files.map((a) => (
            <li key={a.id} className={cn('flex items-center gap-2 rounded-md border border-border-subtle bg-surface px-2.5 py-1.5 text-sm', removing.includes(a.id) && 'opacity-50')}>
              <span className="flex shrink-0 text-fg-subtle [&_svg]:size-4">{iconFor(a)}</span>
              <a href={a.downloadUrl} download={a.name} className="min-w-0 flex-1 truncate text-fg hover:text-accent-text hover:underline" title={`Download ${a.name}`}>
                {a.name}
              </a>
              <span className="shrink-0 text-fg-subtle num">{formatBytes(a.size)}</span>
              <IconButton label={`Download ${a.name}`} icon={<Download />} size="xs" onClick={() => window.open(a.downloadUrl, '_self')} />
              {onRemove && canRemove?.(a) && <IconButton label={`Remove ${a.name}`} icon={<Trash2 />} size="xs" variant="danger" onClick={() => onRemove(a)} disabled={removing.includes(a.id)} />}
            </li>
          ))}
        </ul>
      )}
      {lightbox}
    </div>
  );
}

export interface LightboxProps {
  images: AttachmentView[];
  openId: string | null;
  onOpenChange: (id: string | null) => void;
}

/** One image at full size, with previous and next (arrow keys) and download. */
export function Lightbox({ images, openId, onOpenChange }: LightboxProps) {
  const index = images.findIndex((a) => a.id === openId);
  const a = index >= 0 ? images[index] : undefined;
  const go = (d: number) => {
    if (images.length < 2 || index < 0) return;
    onOpenChange(images[(index + d + images.length) % images.length]!.id);
  };
  useEffect(() => {
    if (!a) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  return (
    <RD.Root open={!!a} onOpenChange={(v) => !v && onOpenChange(null)}>
      <RD.Portal>
        <RD.Overlay className="fixed inset-0 z-(--z-overlay) animate-overlay-in bg-backdrop" />
        <RD.Content
          className="fixed inset-4 z-50 flex animate-pop-in flex-col gap-2 outline-none"
          aria-describedby={undefined}
          tabIndex={-1}
          onOpenAutoFocus={(e) => {
            // Focus the lightbox itself, not its first button (whose tooltip would pop open).
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.focus();
          }}
        >
          {a && (
            <>
              <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-raised px-3 py-2 shadow-overlay">
                <RD.Title className="min-w-0 flex-1 truncate text-base font-medium text-fg">{a.name}</RD.Title>
                <span className="shrink-0 text-sm text-fg-subtle num">
                  {a.width && a.height ? `${a.width} x ${a.height}, ` : ''}
                  {formatBytes(a.size)}
                  {images.length > 1 ? `, ${index + 1} of ${images.length}` : ''}
                </span>
                {images.length > 1 && (
                  <>
                    <IconButton label="Previous image" icon={<ChevronLeft />} shortcut={['left']} onClick={() => go(-1)} />
                    <IconButton label="Next image" icon={<ChevronRight />} shortcut={['right']} onClick={() => go(1)} />
                  </>
                )}
                <IconButton label="Download" icon={<Download />} onClick={() => window.open(a.downloadUrl, '_self')} />
                <RD.Close asChild>
                  <IconButton label="Close" icon={<X />} shortcut={['esc']} />
                </RD.Close>
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <img src={a.url} alt={a.name} className="max-h-full max-w-full rounded-md object-contain shadow-overlay" />
              </div>
            </>
          )}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
